import React, { useState, useContext, useRef, useEffect } from 'react';
import { t } from '@lingui/macro';
import { useQuery } from '@apollo/client';
import { useLocation } from 'react-router-dom';

import Button from '@decisiv/ui-components/lib/components/Button';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import KDSTopNav from '@decisiv/ui-components/lib/components/TopNav';

import { CurrentInvitationContext, logout } from 'utils/Invitation';
import { useCurrentUserContext } from 'utils/User';
import { stripTeamPrefix } from 'utils/teamFromUrl';

import userInfoQuery from 'utils/User/currentUserProfile.graphql';

import ROUTE_NAMES from 'setup/Routing/routeNames';

import useIntersectionObserver from 'utils/useIntersectionObserver';
import useFeatureFlag, { FEATURE_FLAGS } from 'utils/useFeatureFlag';
import FleetManagementInfo from 'components/TopNav/FleetManagementInfo';
import TeamsDropdown from './TeamsDropdown';
import BrandInfo from './BrandInfo';
import CurrentCallInfo from './CurrentCallInfo';
import SearchLink from './SearchLink';
import NotificationCenter from './NotificationCenter';
import UserMenu from './UserMenu';
import AmazonConnectMenu from './AmazonConnectMenu';
import AppsLinks from './AppsLinks';

function TopNav() {
  const [firstBoundingWidth, setFirstBoundingWidth] = useState(null);

  const { id: invitationId, authenticationKey } = useContext(
    CurrentInvitationContext,
  );
  const { id: userId } = useCurrentUserContext();
  const location = useLocation();

  const isManagementSection =
    useFeatureFlag(FEATURE_FLAGS.PD_1188_ROUTE_BASED_FLEET_MANAGEMENT) &&
    stripTeamPrefix(location.pathname).startsWith(ROUTE_NAMES.admin.management);

  const { loading, data } = useQuery(userInfoQuery, {
    fetchPolicy: 'cache-and-network',
    context: { ignoreInvitationAuth: true },
  });

  const showEndSession =
    invitationId || (!loading && data && authenticationKey);

  const ref = useRef(null);

  const isIntersecting = useIntersectionObserver(ref, { threshold: 1.0 });
  const isFullyVisible = isIntersecting && firstBoundingWidth === null;

  /* istanbul ignore next -- @preserve */
  useEffect(() => {
    if (!isIntersecting && !firstBoundingWidth) {
      setFirstBoundingWidth(ref.current?.getBoundingClientRect()?.width);
    }
  }, [isIntersecting, firstBoundingWidth]);

  /* istanbul ignore next -- @preserve */
  useEffect(() => {
    const handleResize = () => {
      if (ref.current?.getBoundingClientRect()?.width > firstBoundingWidth) {
        setFirstBoundingWidth(null);
      }
    };

    if (firstBoundingWidth) {
      window.addEventListener('resize', handleResize);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [firstBoundingWidth]);

  return (
    <div ref={ref}>
      <KDSTopNav>
        <Flex
          css={`
            height: 100%;
          `}
          alignItems="center"
        >
          {isManagementSection ? (
            <>
              <FleetManagementInfo />
              <CurrentCallInfo />
            </>
          ) : (
            <TeamsDropdown>
              <BrandInfo
                variant={
                  isFullyVisible
                    ? 'full'
                    : /* istanbul ignore next -- @preserve */ 'compact'
                }
              />
              <CurrentCallInfo />
            </TeamsDropdown>
          )}
        </Flex>
        <Flex
          css={`
            height: 100%;
          `}
        >
          {userId && (
            <>
              <SearchLink />
              <NotificationCenter />
              <UserMenu
                variant={
                  isFullyVisible
                    ? 'full'
                    : /* istanbul ignore next -- @preserve */ 'compact'
                }
              />
              <AmazonConnectMenu />
              <AppsLinks />
            </>
          )}
          {showEndSession && (
            <Flex alignItems="center" marginRight={2}>
              <Button
                kind="secondary"
                onClick={logout}
                size="small"
                text={t`End Session`}
                variant="inverted"
              />
            </Flex>
          )}
        </Flex>
      </KDSTopNav>
    </div>
  );
}

export default TopNav;
